import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import axios from 'axios';
import { getApiUrl } from "../../config/api";
import { useWebSocket } from "../../contexts/WebSocketContext";

export default function UpcomingEventsWidget({ config = {}, layout = 'grid' }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { on, connected } = useWebSocket();

  const { 
    title = 'Reminders',
    maxEvents = 5,
    daysAhead = 365, // Changed from 30 to 365 to show events up to a year ahead
    showLocation = true,
    showTime = true
  } = config;

  useEffect(() => {
    fetchUpcomingEvents();
  }, [daysAhead]);

  // Listen for real-time calendar updates via WebSocket
  useEffect(() => {
    if (!connected) return;

    const cleanup = on('calendar-updated', (data) => {
      // Refresh events when calendar is updated
      fetchUpcomingEvents();
    });

    return cleanup;
  }, [connected, on]);

  const fetchUpcomingEvents = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const endDate = addDays(today, daysAhead);
            
      let response;
      try {
        response = await axios.get(`${getApiUrl('/api/calendar/events')}?start_date=${format(today, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`);

        if (response.data) {
          const events = response.data
            .filter(event => {
              const eventDate = parseISO(event.event_date);
              return eventDate >= today;
            })
            .sort((a, b) => parseISO(a.event_date) - parseISO(b.event_date))
            .slice(0, 5);

          setEvents(events);
        }
      } catch (error) {
        console.error('Error fetching upcoming events:', error);
        setEvents([]);
      }
    } catch (error) {
      // Suppress all server-related errors from console
      if (!error.message.includes('Server error') && 
          !error.message.includes('Unexpected token') &&
          !error.message.includes('<!DOCTYPE') &&
          !error.message.includes('invalid response format') &&
          !error.message.includes('network connection failed')) {
        console.error('Error fetching upcoming events:', error);
      }
      setError('Calendar service temporarily unavailable');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${
        layout === 'compact' ? 'p-2' : 'p-4'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${
        layout === 'compact' ? 'p-2' : 'p-4'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${
      layout === 'compact' ? 'p-2' : 'p-4'
    }`}>
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>

      {events.length === 0 ? (
        <p className="text-gray-500 text-sm">No upcoming events</p>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <div key={event.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 truncate">{event.name}</h4>
                <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(parseISO(event.event_date), 'MMM d, yyyy')}
                  </span>
                  {showTime && event.start_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {event.start_time}
                    </span>
                  )}
                </div>
                {/* Days until event */}
                <div className="flex items-center gap-1 text-sm text-blue-600 mt-1">
                  <Clock className="h-3 w-3" />
                  {(() => {
                    const today = new Date();
                    const eventDate = parseISO(event.event_date);
                    const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
                    
                    if (daysUntil === 0) {
                      return 'Today';
                    } else if (daysUntil === 1) {
                      return 'Tomorrow';
                    } else if (daysUntil < 0) {
                      return `${Math.abs(daysUntil)} days ago`;
                    } else {
                      return `${daysUntil} days away`;
                    }
                  })()}
                </div>
                {showLocation && event.location && (
                  <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                    <MapPin className="h-3 w-3" />
                    <span className="truncate">{event.location}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 